/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { type ComponentProps, cloneElement, type ReactElement, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { useSideNavContext } from './side-nav-context'
import { SideNavItem } from './side-nav-item'

export interface SideNavSectionProps extends ComponentProps<'div'> {
  /** Optional label shown above the section items. Hidden when the sidebar is collapsed. */
  label?: string
  /** Optional icon displayed to the left of the label. When the sidebar is collapsed, this icon is shown alone. */
  icon?: ReactElement
  /** Optional control or actions aligned to the right of the label row (e.g. a segmented control). */
  labelEnd?: ReactNode
  /** When true, the section is not rendered while the sidebar is collapsed. */
  hideWhenCollapsed?: boolean
  children: ReactNode
}

export function SideNavSection({
  label,
  icon,
  labelEnd,
  hideWhenCollapsed = false,
  children,
  className,
  ...boxProps
}: SideNavSectionProps) {
  const { expanded, setExpanded } = useSideNavContext()
  const labelRowMaxHeight = labelEnd != null ? '52px' : '28px'
  const collapsedIconOnly = !expanded && icon != null && !hideWhenCollapsed

  if (!expanded && hideWhenCollapsed) {
    return null
  }

  return (
    <>
      <div className={cn('my-2', className, collapsedIconOnly && 'hidden')} {...boxProps}>
        {label ? (
          <div
            className="mb-1 overflow-hidden"
            style={{
              maxHeight: expanded ? labelRowMaxHeight : '0px',
              opacity: expanded ? 1 : 0,
              transition: expanded
                ? 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0.05s, opacity 0.15s ease 0.1s'
                : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.08s ease',
            }}
          >
            <div className="flex w-full min-w-0 items-center gap-1.5 pb-1 text-foreground">
              {icon != null ? (
                <span className="inline-flex size-4 shrink-0 items-center justify-center text-muted-foreground">
                  {icon}
                </span>
              ) : null}
              <h3 className="m-0 flex min-w-0 items-center font-semibold text-muted-foreground uppercase tracking-wider md:text-sm">
                {label}
              </h3>
              {labelEnd != null ? <div className="ml-auto shrink-0">{labelEnd}</div> : null}
            </div>
          </div>
        ) : null}
        {children}
      </div>
      {collapsedIconOnly && icon != null ? (
        <SideNavItem icon={cloneElement(icon)} label={label ?? ''} onClick={() => setExpanded(true)} />
      ) : null}
    </>
  )
}
